import { ChatResponseStatus } from "@/components/ai-chat/types";

import type { AgentExecution } from "@/types/agent-execution";
import { AgentExecutionStatus } from "@/types/agent-execution";
import type {
  AskDecodaApprovalOwner,
  ChatHistoryEntry,
} from "@/types/chat-thread";

import type { ApplySettingsPatchResult } from "@/api/ai/page-assistant";

export function settingsApprovalDraftKey(
  tenant: string | undefined,
  owner: AskDecodaApprovalOwner,
) {
  return [
    tenant,
    owner.runtimeEpoch,
    owner.threadId,
    owner.responseChatId,
    owner.checkpointId,
    owner.interrupt.id,
  ].join(":");
}
export function findSettingsApproval(
  history: readonly ChatHistoryEntry[],
  threadId: string,
  patchId: string,
  runtimeEpoch: number,
) {
  for (const entry of history) {
    if (
      entry.status !== ChatResponseStatus.BLOCKED ||
      !entry.runtimeState?.checkpointId
    )
      continue;
    for (const interrupt of entry.runtimeState.interrupts) {
      const actionIndex = interrupt.value.action_requests.findIndex(
        (request) =>
          request.name === "settings_apply_patch" &&
          (request.args.patch_id ?? request.args.patchId) === patchId,
      );
      if (actionIndex >= 0)
        return {
          actionIndex,
          owner: {
            threadId,
            responseChatId: entry.id,
            checkpointId: entry.runtimeState.checkpointId,
            interrupt,
            runtimeEpoch,
          } satisfies AskDecodaApprovalOwner,
        };
    }
  }
  return null;
}
export interface SettingsApplyOutcome {
  kind: "applied" | "partial" | "rejected" | "failed";
  result: ApplySettingsPatchResult;
  appliedTargetIds: string[];
  warnings: string[];
}
export function extractSettingsApplyOutcome(
  execution: AgentExecution,
  patchId: string,
): SettingsApplyOutcome | null {
  if (
    execution.operation !== "settings_apply_patch" ||
    ![AgentExecutionStatus.COMPLETED, AgentExecutionStatus.FAILED].includes(
      execution.status,
    ) ||
    !execution.output ||
    Array.isArray(execution.output)
  )
    return null;
  const input =
    execution.input && !Array.isArray(execution.input) ? execution.input : {};
  const output = execution.output;
  const data =
    output.data &&
    typeof output.data === "object" &&
    !Array.isArray(output.data)
      ? (output.data as Record<string, unknown>)
      : output;
  if (
    (data.patch_id ?? data.patchId ?? input.patch_id ?? input.patchId) !==
    patchId
  )
    return null;
  const status = String(data.status ?? output.message ?? "failed");
  const committed = data.commit_state ?? data.commitState;
  const completed = data.completion_state ?? data.completionState;
  // A failed projection can still carry a committed write or rejection. Only
  // structured terminal engine metadata permits interpreting that projection.
  if (
    execution.status === AgentExecutionStatus.FAILED &&
    (typeof data.status !== "string" ||
      !["not_committed", "committed", "unknown"].includes(String(committed)) ||
      !["pending", "completed", "failed", "unknown"].includes(
        String(completed),
      ))
  )
    return null;
  const success =
    execution.status === AgentExecutionStatus.COMPLETED &&
    output.success === true &&
    data.success === true &&
    committed === "committed" &&
    completed === "completed";
  const changes = data.applied_changes ?? data.appliedChanges;
  const result: ApplySettingsPatchResult = {
    ...data,
    completionId: `${execution.chatHistoryId}:${execution.id}:${execution.attempt}`,
    patchId,
    success,
    status,
    appliedChanges: Array.isArray(changes)
      ? changes.map((change) => ({
          fieldId: change.field_id ?? change.fieldId,
          from: change.from_ ?? change.from,
          to: change.to,
          recordId: change.record_id ?? change.recordId,
        }))
      : undefined,
    createdIds: (data.created_ids ?? data.createdIds) as
      | Record<string, string>
      | undefined,
  };
  const targets = data.applied_target_ids ?? data.appliedTargetIds;
  return {
    kind:
      status === "rejected" &&
      committed === "not_committed" &&
      completed === "completed"
        ? "rejected"
        : success
          ? "applied"
          : committed === "committed" ||
              committed === "unknown" ||
              (Array.isArray(changes) && changes.length > 0)
            ? "partial"
            : "failed",
    result,
    appliedTargetIds: Array.isArray(targets)
      ? targets.filter((id): id is string => typeof id === "string")
      : [],
    warnings: Array.isArray(data.warnings) ? data.warnings.map(String) : [],
  };
}
